import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Wraps <Routes> — every navigation gets:
 * 1. A thin crimson progress bar sweeping across the very top of the viewport.
 * 2. A soft fade + rise entrance on the new page (exit fades out fast).
 * Also scrolls back to top so the new page always opens at its header.
 */
export default function PageTransition({ children }) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <>
      <motion.div
        key={`progress-${location.pathname}${location.search}`}
        className="fixed top-0 left-0 h-[2px] bg-[#D91E2A] z-[100] pointer-events-none"
        initial={{ width: "0%", opacity: 1 }}
        animate={{ width: "100%", opacity: [1, 1, 0] }}
        transition={{ duration: 0.55, times: [0, 0.7, 1], ease: "easeOut" }}
        data-testid="route-progress-bar"
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          data-testid="page-transition-wrapper"
        >
          {React.isValidElement(children)
            ? React.cloneElement(children, { location })
            : children}
        </motion.div>
      </AnimatePresence>
    </>
  );
}
